import React, { useState, useEffect } from 'react';
import { Upload, Download, FileText, CheckCircle2, AlertCircle, X, CheckSquare, Square } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { programsAPI } from '../services/api';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImportAthletes = ({ onClose, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await programsAPI.getAll();
        setPrograms(res.data);
      } catch (err) {
        console.error('Failed to load programs:', err);
      }
    };
    fetchPrograms();
  }, []);

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/csv/sample-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'athlete_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'Template downloaded successfully!' });
    } catch (error) {
      console.error('Error downloading template:', error);
      toast({ title: 'Error downloading template', variant: 'destructive' });
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast({ title: 'Please select a CSV file', variant: 'destructive' });
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast({ title: 'Please select a file first', variant: 'destructive' });
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);

      // Add program override if programs are selected
      if (selectedProgramIds.length > 0) {
        formData.append('program_ids', selectedProgramIds.join(','));
      }

      const response = await axios.post(`${API}/csv/import-athletes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
      
      if (response.data.imported_count > 0) {
        toast({ 
          title: `Successfully imported ${response.data.imported_count} athlete(s)!`,
          description: response.data.error_count > 0 ? `${response.data.error_count} rows had errors` : undefined
        });
        
        if (onImportComplete) {
          onImportComplete();
        }
      } else {
        toast({ 
          title: 'No athletes imported', 
          description: 'Please check the error details',
          variant: 'destructive' 
        });
      }
    } catch (error) {
      console.error('Error importing athletes:', error);
      toast({ 
        title: 'Error importing file', 
        description: error.response?.data?.detail || error.message,
        variant: 'destructive' 
      });
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
  };

  return (
    <div className="space-y-6">
      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Import Athletes from CSV</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">How to Import:</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
              <li>Download the CSV template below</li>
              <li>Fill in your athlete information</li>
              <li>Save the file and upload it here</li>
              <li>Review the import results</li>
            </ol>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              className="flex-1"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Program Assignment Override */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Assign to Program(s)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-600">
            Select which program(s) to assign imported athletes to. This overrides any program names in the CSV file.
          </p>
          <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
            {programs.length === 0 ? (
              <p className="text-sm text-gray-400 p-3">No programs found. Create a program first.</p>
            ) : (
              programs.map(p => (
                <label
                  key={p.id}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                  data-testid={`import-program-${p.id}`}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedProgramIds(prev =>
                        prev.includes(p.id) ? prev.filter(id => id !== p.id) : [...prev, p.id]
                      );
                    }}
                    className="flex-shrink-0"
                  >
                    {selectedProgramIds.includes(p.id) ? (
                      <CheckSquare className="h-5 w-5 text-blue-600" />
                    ) : (
                      <Square className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color || '#0067B9' }} />
                    <span className="text-sm font-medium">{p.name}</span>
                    <span className="text-xs text-gray-400">{p.sport}</span>
                  </div>
                </label>
              ))
            )}
          </div>
          {selectedProgramIds.length > 0 && (
            <p className="text-xs text-blue-600 font-medium">
              {selectedProgramIds.length} program{selectedProgramIds.length !== 1 ? 's' : ''} selected — will override CSV programs column
            </p>
          )}
          {selectedProgramIds.length === 0 && (
            <p className="text-xs text-gray-400">
              No programs selected — CSV's "Programs" column will be used
            </p>
          )}
        </CardContent>
      </Card>

      {/* Upload Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Upload CSV File</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              id="csv-upload"
            />
            <label htmlFor="csv-upload" className="cursor-pointer">
              <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-sm text-gray-600 mb-2">
                {file ? (
                  <span className="flex items-center justify-center space-x-2">
                    <FileText className="h-4 w-4" />
                    <span className="font-medium">{file.name}</span>
                  </span>
                ) : (
                  'Click to select CSV file or drag and drop'
                )}
              </p>
              <p className="text-xs text-gray-500">CSV files only</p>
            </label>
          </div>

          <div className="flex space-x-3">
            {file && (
              <Button
                variant="outline"
                onClick={handleReset}
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                Clear
              </Button>
            )}
            <Button
              onClick={handleImport}
              disabled={!file || importing}
              className="flex-1"
              style={{ backgroundColor: '#0067B9' }}
            >
              {importing ? 'Importing...' : 'Import Athletes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Card */}
      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Import Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-900">Imported</span>
                </div>
                <p className="text-2xl font-bold text-green-700">{result.imported_count}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <span className="font-semibold text-red-900">Errors</span>
                </div>
                <p className="text-2xl font-bold text-red-700">{result.error_count}</p>
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                <h4 className="font-semibold text-red-900 mb-2">Error Details:</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {result.errors.map((error, index) => (
                    <div key={index} className="text-sm text-red-800">
                      <span className="font-medium">Row {error.row}:</span> {error.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleReset}
                className="flex-1"
              >
                Import Another File
              </Button>
              {onClose && (
                <Button
                  onClick={onClose}
                  className="flex-1"
                  style={{ backgroundColor: '#0067B9' }}
                >
                  Done
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ImportAthletes;